import React from 'react';
import { Box, Avatar, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import { avatarSizes } from '../theme/commonStyles';
import { POSITION_MAP } from '../constants/game';
import SidelinedIcon from './SidelinedIcon';

/**
 * Centraal speler-kaartje voor de mobiele weergave en de pop-ups.
 *
 * Het linker/midden-deel (afbeelding, clublogo-badge, naam, positie-badge,
 * clubnaam en het blessure/schorsing-icoon) is overal gelijk. Het variabele
 * rechterdeel en eventuele extra regels/rijen worden via slots meegegeven, zodat
 * elk scherm zijn eigen elementen (punten, prijs, knoppen, rondepunten) kwijt kan
 * zonder het kaartje zelf te dupliceren.
 *
 * Puur presentationeel: haalt zelf geen data op. De blessure/schorsing-status
 * komt via `status` binnen (zie `useSidelinedMap`).
 *
 * @param {object}    props
 * @param {object}    props.player      Speler met `photoUrl`, `clubLogo`, `name`, `clubName`, `position`.
 * @param {boolean}   [props.isCaptain] Toont `(c)` achter de naam.
 * @param {object|null} [props.status]  Sidelined-record voor het icoon/tooltip.
 * @param {React.ReactNode} [props.leadingSlot] Element links van de afbeelding (bijv. een in/uit-pijl).
 * @param {React.ReactNode} [props.rightSlot]  Variabel rechterdeel (punten/prijs, knoppen).
 * @param {React.ReactNode} [props.subCaption] Extra regel onder de clubnaam.
 * @param {React.ReactNode} [props.footer]     Rij onder de hoofd-rij, binnen de rand (bijv. Collapse of knop).
 * @param {Function} [props.onClick]    Maakt het kaartje klikbaar.
 * @param {string}   [props.bgcolor]    Achtergrondtint van de hoofd-rij (bijv. positie-kleur).
 * @param {object}   [props.sx]         Extra styling voor de buitenste rand-Box.
 */
const PlayerCard = React.forwardRef(function PlayerCard({
    player,
    isCaptain = false,
    status = null,
    leadingSlot = null,
    rightSlot = null,
    subCaption = null,
    footer = null,
    onClick = null,
    bgcolor,
    sx,
}, ref) {
    const theme = useTheme();

    if (!player) return null;

    const dutchPosition = POSITION_MAP[player.position] || player.position;
    const positionStyle = dutchPosition ? theme.positions[dutchPosition] : null;

    return (
        <Box
            ref={ref}
            onClick={onClick || undefined}
            sx={{
                border: '1px solid',
                borderColor: 'rgba(255, 255, 255, 0.08)',
                borderRadius: 1,
                overflow: 'hidden',
                ...(onClick ? { cursor: 'pointer' } : {}),
                ...sx,
            }}
        >
            <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2, ...(bgcolor ? { bgcolor } : {}) }}>
                {leadingSlot}
                <Box sx={{ position: 'relative' }}>
                    <Avatar src={player.photoUrl} alt={player.name} sx={avatarSizes.lg}>
                        <SportsSoccerIcon />
                    </Avatar>
                    {player.clubLogo && (
                        <Box
                            component="img"
                            src={player.clubLogo}
                            alt={`${player.clubName || ''} logo`}
                            sx={{
                                position: 'absolute',
                                bottom: -4,
                                right: -6,
                                ...avatarSizes.sm,
                                objectFit: 'contain',
                            }}
                        />
                    )}
                </Box>

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                            {player.name} {isCaptain && '(c)'}
                        </Typography>
                        {positionStyle && (
                            <Box sx={{
                                ...positionStyle,
                                color: 'common.white',
                                width: 20,
                                height: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 1,
                                fontWeight: 'bold',
                                fontSize: '0.75rem',
                                flexShrink: 0,
                            }}>
                                {dutchPosition.charAt(0).toUpperCase()}
                            </Box>
                        )}
                        <SidelinedIcon status={status} />
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>
                        {player.clubName}
                    </Typography>
                    {subCaption}
                </Box>

                {rightSlot}
            </Box>
            {footer}
        </Box>
    );
});

export default PlayerCard;
